import { useCallback, useEffect, useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import {
  getTournamentSeries,
  patchTournamentSeries,
  endTournamentSeries,
  listTables,
  getAdminUser,
  ApiError,
} from '../../lib/api';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DatePicker } from '../../components/DatePicker';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { formatClock } from '../../lib/time';
import { formatGel } from '../../lib/format';
import { fullName } from '../../lib/format';
import {
  feeGelFromMinor,
  formatRatingWindow,
  tableNamesLabel,
  TTAR_RESULT_INPUT_MODE_LABELS,
  tournamentStatusColor,
  tournamentStatusLabel,
} from '../../lib/tournaments';
import {
  tournamentSeriesRecurrenceLabel,
  tournamentSeriesStatusColor,
  tournamentSeriesStatusLabel,
  seriesConflictSummary,
} from '../../lib/tournament-series';
import {
  EMPTY_TOURNAMENT_SERIES_FORM,
  buildPatchTournamentSeriesBody,
  formFromTournamentSeries,
  tournamentSeriesFormErrors,
  type TournamentSeriesFormValue,
} from '../../lib/tournament-series-form';
import type {
  AdminLookupUser,
  AdminTournamentSeries,
  AdminTournamentSeriesDetailResponse,
  AdminTournamentSeriesEditResponse,
  TableEmbed,
} from '../../lib/types';
import { TournamentSeriesFieldsForm } from './TournamentSeriesFieldsForm';
import { SeriesEditOutcome } from './SeriesEditOutcome';
import { orderByDateDescending, orderByDateTimeDescending } from '../../lib/admin-list-order';
import { shouldKeepNestedAction } from '../../components/DataTable';

export function TournamentSeriesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();

  const seriesId = id ? Number(id) : NaN;
  const validId = Number.isFinite(seriesId);

  const detailLoader = useCallback(
    (signal: AbortSignal) => getTournamentSeries(seriesId, signal),
    [seriesId],
  );
  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);

  const detail = useApi<AdminTournamentSeriesDetailResponse>(detailLoader);
  const tables = useApi<TableEmbed[]>(tablesLoader);

  const createdById = detail.data?.series.created_by ?? null;
  const creatorLoader = useCallback(
    (signal: AbortSignal) =>
      createdById ? getAdminUser(createdById, signal) : Promise.resolve(null),
    [createdById],
  );
  const creator = useApi<AdminLookupUser | null>(creatorLoader);
  const upcomingOccurrences = useMemo(
    () => orderByDateTimeDescending(
      detail.data?.upcoming_occurrences ?? [],
      (occurrence) => ({ date: occurrence.date, time: occurrence.start_time, id: occurrence.id }),
    ),
    [detail.data?.upcoming_occurrences],
  );
  const recentSkips = useMemo(
    () => orderByDateDescending(
      detail.data?.recent_skips ?? [],
      (skip) => ({ date: skip.date, id: skip.date }),
    ),
    [detail.data?.recent_skips],
  );

  // Local copy of the series row so a PATCH/end response can update the
  // screen immediately without a second round trip — same pattern as
  // `TournamentDetailPage`. Seeded from `detail.data` on first load.
  const [series, setSeries] = useState<AdminTournamentSeries | null>(null);
  const [form, setForm] = useState<TournamentSeriesFormValue>(EMPTY_TOURNAMENT_SERIES_FORM);
  const [originalForm, setOriginalForm] = useState<TournamentSeriesFormValue>(EMPTY_TOURNAMENT_SERIES_FORM);
  const [saving, setSaving] = useState(false);
  const [editOutcome, setEditOutcome] = useState<AdminTournamentSeriesEditResponse | null>(null);

  const [endDate, setEndDate] = useState('');
  const [ending, setEnding] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  useEffect(() => {
    if (detail.data) {
      setSeries(detail.data.series);
      const f = formFromTournamentSeries(detail.data.series);
      setForm(f);
      setOriginalForm(f);
    }
  }, [detail.data]);

  const patch = (p: Partial<TournamentSeriesFormValue>) => setForm((f) => ({ ...f, ...p }));

  if (!validId) {
    return <div style={{ ...T.ui, color: PS.danger }}>Invalid series id.</div>;
  }

  const patchBody = buildPatchTournamentSeriesBody(originalForm, form);
  const hasChanges = Object.keys(patchBody).length > 0;
  const formErrors = tournamentSeriesFormErrors(form);
  const canSave = hasChanges && formErrors.length === 0 && !saving;

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!series || !canSave) return;
    setSaving(true);
    setEditOutcome(null);
    try {
      const res = await patchTournamentSeries(series.id, patchBody);
      setSeries(res.series);
      const f = formFromTournamentSeries(res.series);
      setForm(f);
      setOriginalForm(f);
      setEditOutcome(res);
      push(
        'success',
        `Series updated. ${res.updated_occurrences.length} occurrence${res.updated_occurrences.length === 1 ? '' : 's'} updated` +
          (res.skipped_occurrences.length > 0
            ? `, ${res.skipped_occurrences.length} left unchanged — see below.`
            : '.'),
      );
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setForm(originalForm);
    setEditOutcome(null);
  };

  const handleEnd = async () => {
    if (!series) return;
    setEnding(true);
    try {
      const res = await endTournamentSeries(series.id, { ends_on: endDate || null });
      setSeries(res.series);
      const f = formFromTournamentSeries(res.series);
      setForm(f);
      setOriginalForm(f);
      setEndOpen(false);
      setEndDate('');
      push(
        'success',
        `Series ended${res.series.ends_on ? ` as of ${res.series.ends_on}` : ''}. Already-materialized occurrences are untouched.`,
      );
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'End failed');
    } finally {
      setEnding(false);
    }
  };

  const title = series ? series.translations.en?.title ?? series.title : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>
          {title || (series ? `Series #${series.id}` : '')}
        </h1>
        {series && <StatusBadge status={series.status} />}
        <Button variant="secondary" onClick={() => navigate('/tournaments/series')}>
          ← Back
        </Button>
      </header>

      {detail.loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}

      {detail.error && (
        <div
          style={{
            padding: 12,
            background: PS.errorBg,
            border: `1px solid ${PS.errorBorder}`,
            color: PS.errorText,
            borderRadius: 3,
          }}
        >
          {detail.error.message}
        </div>
      )}

      {detail.data && series && (
        <>
          <section
            style={{
              background: PS.white,
              border: `1px solid ${PS.borderLight}`,
              borderRadius: 4,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <h2 style={{ ...T.subheading, margin: 0 }}>Series</h2>
            <Row label="ID" value={`#${series.id}`} />
            <Row label="Cadence" value={tournamentSeriesRecurrenceLabel(series.day_of_week)} />
            <Row label="Time" value={`${formatClock(series.start_time)}–${formatClock(series.end_time)}`} />
            <Row label="Warmup" value={`${series.warmup_minutes} min`} />
            <Row label="Tables" value={tableNamesLabel(series.table_ids, tables.data ?? [])} />
            <Row label="Fee" value={formatGel(feeGelFromMinor(series.fee_minor))} />
            <Row
              label="Capacity"
              value={series.max_participants != null ? String(series.max_participants) : 'Unlimited'}
            />
            <Row
              label="TTAR rating"
              value={formatRatingWindow(series.min_ttar_rating, series.max_ttar_rating)}
            />
            <Row label="TTAR results" value={TTAR_RESULT_INPUT_MODE_LABELS[series.ttar_result_input_mode]} />
            <Row label="Starts on" value={series.starts_on} />
            <Row label="Ends on" value={series.ends_on ?? '— (open-ended)'} />
            <Row
              label="Occurrences"
              value={`${detail.data.occurrence_count} materialized total · window ${detail.data.upcoming_window.start_date} – ${detail.data.upcoming_window.end_date}`}
            />
            <Row
              label="Created by"
              value={
                creator.data
                  ? creator.data.phone
                    ? `${fullName(creator.data)} · ${creator.data.phone}`
                    : fullName(creator.data)
                  : `#${series.created_by}`
              }
            />
            {series.image_url && (
              <Row
                label="Image"
                value={
                  <a href={series.image_url} target="_blank" rel="noreferrer" style={{ color: PS.linkBlue }}>
                    {series.image_url}
                  </a>
                }
              />
            )}

            <div
              style={{
                marginTop: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                borderTop: `1px dashed ${PS.borderLight}`,
                paddingTop: 12,
              }}
            >
              <span style={{ ...T.label }}>End series</span>
              <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
                Stops future materialization only. Every already-materialized occurrence stays
                exactly as it is — nothing is cancelled.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <DatePicker value={endDate} onChange={setEndDate} ariaLabel="End date" />
                <Button
                  variant="danger"
                  onClick={() => setEndOpen(true)}
                  disabled={series.status === 'ended' || ending}
                >
                  {series.status === 'ended' ? 'Already ended' : ending ? 'Ending…' : 'End series'}
                </Button>
              </div>
            </div>
          </section>

          <section>
            <h2 style={{ ...T.subheading, margin: '0 0 8px' }}>
              Upcoming occurrences ({upcomingOccurrences.length})
            </h2>
            {upcomingOccurrences.length === 0 ? (
              <div style={{ ...T.caption, color: PS.textSec }}>
                No materialized occurrences in the upcoming window.
              </div>
            ) : (
              <ScrollTable>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                      <th style={th}>Date</th>
                      <th style={th}>Time</th>
                      <th style={th}>Status</th>
                      <th style={th}>Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingOccurrences.map((o) => (
                      <tr
                        key={o.id}
                        tabIndex={0}
                        aria-label={`Open tournament ${o.title || o.id}`}
                        onClick={(event) => {
                          if (shouldKeepNestedAction(event)) return;
                          navigate(`/tournaments/${o.id}`);
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          if (shouldKeepNestedAction(event)) return;
                          navigate(`/tournaments/${o.id}`);
                        }}
                        style={{ borderBottom: `1px solid ${PS.borderLight}`, cursor: 'pointer' }}
                      >
                        <td style={td}>{o.date}</td>
                        <td style={td}>
                          {formatClock(o.start_time)}–{formatClock(o.end_time)}
                        </td>
                        <td style={td}>
                          <span style={{ ...T.microLabel, color: tournamentStatusColor(o.status) }}>
                            {tournamentStatusLabel(o.status)}
                          </span>
                        </td>
                        <td style={td}>{formatGel(feeGelFromMinor(o.fee_minor))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollTable>
            )}
          </section>

          <section>
            <h2 style={{ ...T.subheading, margin: '0 0 8px' }}>
              Recent skipped dates ({recentSkips.length})
            </h2>
            {recentSkips.length === 0 ? (
              <div style={{ ...T.caption, color: PS.textSec }}>No skipped dates recorded.</div>
            ) : (
              <ScrollTable>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                      <th style={th}>Date</th>
                      <th style={th}>Conflict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentSkips.map((s) => (
                      <tr key={s.date} style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                        <td style={td}>{s.date}</td>
                        <td style={{ ...td, color: PS.errorText }}>
                          {seriesConflictSummary(s.conflict_detail)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollTable>
            )}
          </section>

          <section
            style={{
              background: PS.white,
              border: `1px solid ${PS.borderLight}`,
              borderRadius: 4,
              padding: 16,
            }}
          >
            <h2 style={{ ...T.subheading, margin: '0 0 12px' }}>Edit</h2>
            <form onSubmit={(e) => void handleSave(e)} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <TournamentSeriesFieldsForm
                value={form}
                onChange={patch}
                tables={tables.data ?? []}
                tablesLoading={tables.loading}
              />

              {formErrors.length > 0 && (
                <ul style={{ ...T.caption, color: PS.errorText, margin: 0, paddingLeft: 18 }}>
                  {formErrors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="submit" disabled={!canSave}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleDiscard} disabled={!hasChanges || saving}>
                  Discard changes
                </Button>
              </div>
            </form>

            {editOutcome && (
              <div style={{ marginTop: 16 }}>
                <SeriesEditOutcome
                  updated={editOutcome.updated_occurrences}
                  skipped={editOutcome.skipped_occurrences}
                />
              </div>
            )}
          </section>
        </>
      )}

      <ConfirmDialog
        open={endOpen}
        title="End this series?"
        message={
          endDate
            ? `Stops materializing new occurrences after ${endDate}. Every occurrence already materialized (before or after that date) stays exactly as it is — nothing is cancelled.`
            : 'Stops materializing new occurrences immediately. Every already-materialized occurrence stays exactly as it is — nothing is cancelled. This cannot be undone.'
        }
        confirmLabel="End series"
        destructive
        onConfirm={handleEnd}
        onCancel={() => setEndOpen(false)}
      />
    </div>
  );
}

function ScrollTable({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: PS.white,
        border: `1px solid ${PS.borderLight}`,
        borderRadius: 4,
        overflowX: 'auto',
        // overflow-x:auto implicitly makes overflow-y `auto`, which traps
        // vertical page scroll on touch. Pin it to hidden.
        overflowY: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

const th: CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontFamily: "'Barlow Condensed',sans-serif",
  fontSize: 12,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: PS.textSec,
};

const td: CSSProperties = {
  padding: '10px 12px',
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  color: PS.nearBlack,
};

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <div style={{ ...T.label, width: 160, flexShrink: 0 }}>{label}</div>
      <div style={{ ...T.ui, color: PS.nearBlack, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: AdminTournamentSeries['status'] }) {
  const color = tournamentSeriesStatusColor(status);
  return (
    <span
      style={{
        ...T.microLabel,
        color,
        background: PS.white,
        border: `1px solid ${color}`,
        borderRadius: 3,
        padding: '2px 8px',
      }}
    >
      {tournamentSeriesStatusLabel(status)}
    </span>
  );
}
